import type { Href } from 'expo-router';
import { StyleSheet, View } from 'react-native';

import { Text } from '@/components/Text';
import { colors, fontSizes, radii, spacing } from '@/constants/theme';
import type { ShippingTracker as Tracker } from '@/types/deal';

import { TrackPackageCard } from './TrackPackageCard';

export type ShippingTrackerProps = {
  tracker: Tracker;
  /** The deal's Track package page; without it the card opens the carrier's site. */
  trackHref?: Href;
  testID?: string;
};

/**
 * MarketPryce Social "Get your product": where the order is (Order →
 * Preparing to ship → Shipped → Delivered), then the carrier card once it
 * ships. The server builds every stage and label; this only draws them.
 * Reached stages and the line between them are Electric Green; the current
 * stage's label is bold Deep Green.
 */
export function ShippingTracker({ tracker, trackHref, testID }: ShippingTrackerProps) {
  const { stages, tracking, note } = tracker;
  const last = stages.length - 1;

  return (
    <View style={styles.container} testID={testID}>
      <View style={styles.stages} accessibilityRole="progressbar" accessibilityLabel={currentLabel(tracker)}>
        {stages.map((stage, i) => (
          <View key={stage.label} style={styles.stage} testID={testID ? `${testID}-stage-${i}` : undefined}>
            <View style={styles.dotRow}>
              <View style={[styles.line, i === 0 && styles.lineHidden, stage.reached && styles.lineReached]} />
              <View style={[styles.dot, stage.reached && styles.dotReached]} />
              <View
                style={[styles.line, i === last && styles.lineHidden, stages[i + 1]?.reached && styles.lineReached]}
              />
            </View>
            <Text
              variant={stage.current ? 'captionBold' : 'caption'}
              color={stage.current ? 'deepGreen' : 'textMuted'}
              align="center"
              style={styles.label}
              testID={testID ? `${testID}-label-${i}` : undefined}
            >
              {stage.label}
            </Text>
          </View>
        ))}
      </View>

      {note ? (
        <Text variant="caption" color="textMuted" testID={testID ? `${testID}-note` : undefined}>
          {note}
        </Text>
      ) : null}

      {tracking ? (
        <TrackPackageCard tracking={tracking} trackHref={trackHref} testID={testID ? `${testID}-tracking` : undefined} />
      ) : null}
    </View>
  );
}

function currentLabel(tracker: Tracker): string {
  const current = tracker.stages.find((s) => s.current);
  return current ? `Shipping: ${current.label}` : 'Shipping';
}

const DOT_SIZE = 12;
const LINE_HEIGHT = 2;

const styles = StyleSheet.create({
  container: {
    gap: spacing.md,
    marginTop: spacing.sm,
  },
  stages: {
    flexDirection: 'row',
  },
  stage: {
    flex: 1,
    alignItems: 'center',
    gap: spacing.xs,
  },
  dotRow: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'stretch',
  },
  line: {
    flex: 1,
    height: LINE_HEIGHT,
    backgroundColor: colors.border,
  },
  lineReached: {
    backgroundColor: colors.electricGreen,
  },
  // Keeps the dot centered in its column on the first and last stage.
  lineHidden: {
    opacity: 0,
  },
  // Not reached yet: a flat grey ring (no white fill, which reads as a
  // raised button on the page).
  dot: {
    width: DOT_SIZE,
    height: DOT_SIZE,
    borderRadius: radii.full,
    backgroundColor: colors.border,
    borderWidth: LINE_HEIGHT,
    borderColor: colors.trackMutedFill,
  },
  dotReached: {
    backgroundColor: colors.electricGreen,
    borderColor: colors.electricGreen,
  },
  // 12pt, a step down from the step's 14pt copy (four labels share the row).
  label: {
    paddingHorizontal: spacing.xs,
    fontSize: fontSizes.xs,
    lineHeight: 16,
  },
});
